import React from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CourseCompletionCelebrationProps {
  isOpen: boolean;
  courseTitle: string;
  onClose: () => void;
  onViewTranscript: () => void;
}

// Lightweight CSS-only celebration (no confetti dependency is installed). A
// burst of animated confetti pieces is drawn behind a congratulatory dialog.
const CONFETTI_PIECES = Array.from({ length: 24 });

export function CourseCompletionCelebration({
  isOpen,
  courseTitle,
  onClose,
  onViewTranscript,
}: CourseCompletionCelebrationProps) {
  return (
    <Dialog className="relative z-40" onClose={onClose} open={isOpen}>
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />

      {isOpen && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-40 overflow-hidden"
        >
          {CONFETTI_PIECES.map((_, index) => {
            const left = (index * 37) % 100;
            const delay = (index % 8) * 0.15;
            const duration = 2.5 + (index % 5) * 0.4;
            const colors = [
              "#22c55e",
              "#3b82f6",
              "#f59e0b",
              "#ec4899",
              "#8b5cf6",
            ];
            return (
              <span
                className="course-confetti-piece absolute top-[-10%] h-2 w-2 rounded-sm"
                key={index}
                style={{
                  left: `${left}%`,
                  backgroundColor: colors[index % colors.length],
                  animationDelay: `${delay}s`,
                  animationDuration: `${duration}s`,
                }}
              />
            );
          })}
        </div>
      )}

      <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
        <DialogPanel className="relative w-full max-w-md overflow-hidden rounded-lg bg-white dark:bg-slate-800 p-6 text-center shadow-xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
            <Trophy className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <DialogTitle
            as="h3"
            className="text-xl font-semibold text-gray-900 dark:text-white"
          >
            Congratulations!
          </DialogTitle>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            You&apos;ve completed{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {courseTitle}
            </span>
            . Nice work.
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={onViewTranscript}>View my courses</Button>
            <Button onClick={onClose} variant="outline">
              Keep reviewing
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default CourseCompletionCelebration;
